import React from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { Checkbox } from '@atlaskit/checkbox';
import { FormValues, SUCCESS_CHAMPION_PREFERENCES } from '../../../types/ChampionApplicationFormTypes';

const PRIVACY_POLICY_URL = 'https://www.atlassian.com/legal/privacy-policy#what-this-policy-covers';
const NDA_URL =
  'https://atlassian--uat.sandbox.my.site.com/atlassianchampions/resource/1743746183000/ChampionsNDA';

type FieldErrors = {
  consentProgramAgreement?: string;
  consentPrivacyPolicy?: string;
  consentNda?: string;
  consentSuccessChampionAgreement?: string;
};

type Props = {
  values: FormValues;
  updateField: <K extends keyof FormValues>(key: K, value: FormValues[K]) => void;
  errors?: FieldErrors;
};

export const AgreementsSection: React.FC<Props> = ({ values, updateField, errors }) => {
  const showSuccessChampionDataAgreement =
    values.championPreference !== null &&
    SUCCESS_CHAMPION_PREFERENCES.includes(values.championPreference);

  const privacyLabel = (
    <>
      I acknowledge the{' '}
      <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" css={inlineLinkStyles}>
        Atlassian Privacy Policy
      </a>
    </>
  );

  const ndaLabel = (
    <>
      By checking this box, I acknowledge and agree that I am electronically signing the{' '}
      <a href={NDA_URL} target="_blank" rel="noopener noreferrer" css={inlineLinkStyles}>
        Non-Disclosure Agreement (NDA)
      </a>
      {
        ' and that this electronic signature is the legal equivalent of my manual signature on the NDA. NDAs allow Atlassian Champions to receive early product news and access to feedback opportunities.'
      }
    </>
  );

  return (
    <div css={sectionBoxStyles}>
      <div css={controlTopStyles}>
        <div
          css={agreementRowStyles}
          className={
            errors?.consentProgramAgreement
              ? 'champion-form-agreements champion-form-agreements-program--error'
              : 'champion-form-agreements'
          }
        >
          <div css={checkboxRowStyles} className="champion-form-agreements-checkbox-row">
            <abbr css={requiredStyles} title="required" aria-hidden="true">
              *
            </abbr>
            <Checkbox
              name="consent_program_agreement"
              label={
                <>
                  By checking this box, I acknowledge and agree to the Atlassian Champion Program Agreement.
                </>
              }
              isChecked={values.consentProgramAgreement}
              onChange={() =>
                updateField('consentProgramAgreement', !values.consentProgramAgreement)
              }
            />
          </div>
        </div>

        <div
          css={agreementRowTopStyles}
          className={
            errors?.consentPrivacyPolicy
              ? 'champion-form-agreements champion-form-agreements-privacy--error'
              : 'champion-form-agreements'
          }
        >
          <div css={checkboxRowStyles} className="champion-form-agreements-checkbox-row">
            <abbr css={requiredStyles} title="required" aria-hidden="true">
              *
            </abbr>
            <Checkbox
              name="consent_privacy_policy"
              label={privacyLabel}
              isChecked={values.consentPrivacyPolicy}
              onChange={() => updateField('consentPrivacyPolicy', !values.consentPrivacyPolicy)}
            />
          </div>
        </div>

        <div
          css={agreementRowTopStyles}
          className={
            errors?.consentNda
              ? 'champion-form-agreements champion-form-agreements-nda--error'
              : 'champion-form-agreements'
          }
        >
          <div css={checkboxRowStyles} className="champion-form-agreements-checkbox-row">
            <abbr css={requiredStyles} title="required" aria-hidden="true">
              *
            </abbr>
            <Checkbox
              name="consent_nda"
              label={ndaLabel}
              isChecked={values.consentNda}
              onChange={() => updateField('consentNda', !values.consentNda)}
            />
          </div>
        </div>

        {showSuccessChampionDataAgreement && (
          <div
            css={agreementRowTopStyles}
            className={
              errors?.consentSuccessChampionAgreement
                ? 'champion-form-agreements champion-form-agreements-success-champion-data--error'
                : 'champion-form-agreements'
            }
          >
            <div css={checkboxRowStyles} className="champion-form-agreements-checkbox-row">
              <abbr css={requiredStyles} title="required" aria-hidden="true">
                *
              </abbr>
              <Checkbox
                name="consent_success_champion_agreement"
                label={
                  <>
                    Atlassian may use the information provided by me, together with my profile and activity on the
                    Atlassian platform, to confirm my affiliation with an active Atlassian account and support me as a
                    Success Champion.
                  </>
                }
                isChecked={values.consentSuccessChampionAgreement}
                onChange={() =>
                  updateField(
                    'consentSuccessChampionAgreement',
                    !values.consentSuccessChampionAgreement,
                  )
                }
              />
            </div>
          </div>
        )}
      </div>

      {(errors?.consentProgramAgreement ||
        errors?.consentPrivacyPolicy ||
        errors?.consentNda ||
        errors?.consentSuccessChampionAgreement) && (
        <div css={errorStyles}>
          {errors.consentProgramAgreement ||
            errors.consentPrivacyPolicy ||
            errors.consentNda ||
            errors.consentSuccessChampionAgreement}
        </div>
      )}
    </div>
  );
};

const sectionBoxStyles = css({
  marginBottom: '40px',
});

const controlTopStyles = css({
  marginTop: token('space.050'),
});

const checkboxRowStyles = css({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: '0.25rem',
});

const agreementRowStyles = css({
  display: 'flex',
  flexDirection: 'column',
});

const agreementRowTopStyles = css({
  display: 'flex',
  flexDirection: 'column',
  marginTop: token('space.050'),
});

const requiredStyles = css({
  color: 'var(--champion-form-error)',
  textDecoration: 'none',
  marginRight: '0.125rem',
});

const inlineLinkStyles = css({
  color: 'var(--champion-form-blue)',
  textDecoration: 'none',
});

const errorStyles = css({
  color: 'var(--champion-form-error)',
  fontFamily: 'sans-serif',
  fontSize: '0.75rem',
  fontWeight: 400,
  marginTop: token('space.050'),
});
